import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Save } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { useOrganization } from '../../hooks/useOrganization';
import { formatCurrency } from '../../utils/currency';
import VehicleSelector from '../jobcards/VehicleSelector';

const QuotationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { organization } = useOrganization();

  const [formData, setFormData] = useState({
    customerId: '',
    vehicleId: '',
    vehicleNumber: '',
    vinNumber: '',
    odometerReading: '',
    jobCardId: '',
    branchId: '',
    quotationDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    notes: '',
    termsConditions: '',
    discount: 0
  });

  const [items, setItems] = useState([
    { type: 'part', itemId: '', description: '', quantity: 1, price: 0, total: 0 }
  ]);

  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [branches, setBranches] = useState([]);
  const [parts, setParts] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAllJobCards(); // Fetch all job cards initially
    if (isEdit) {
      fetchQuotation();
    }
  }, [id]);

  const fetchAllJobCards = async () => {
    try {
      const response = await axios.get('/api/job-cards/available');
      const jobCardsData = response.data || [];
      setJobCards(jobCardsData);
    } catch (error) {
    }
  };

  const fetchData = async () => {
    try {
      const [customersRes, branchesRes, partsRes, servicesRes] = await Promise.all([
        axios.get('/api/customers'),
        axios.get('/api/branches'),
        axios.get('/api/parts'),
        axios.get('/api/services')
      ]);
      setCustomers(customersRes.data.customers || customersRes.data || []);
      setBranches(branchesRes.data || []);
      setParts(partsRes.data.parts || partsRes.data || []);
      setServices(servicesRes.data.services || servicesRes.data || []);
    } catch (error) {
      toast.error('Failed to load form data');
    }
  };

  const fetchQuotation = async () => {
    try {
      const response = await axios.get(`/api/quotations/${id}`);
      const quotation = response.data;
      setFormData({
        customerId: quotation.customerId,
        vehicleId: quotation.vehicleId || '',
        vehicleNumber: quotation.vehicleNumber || '',
        vinNumber: quotation.vinNumber || '',
        odometerReading: quotation.odometerReading || '',
        jobCardId: quotation.jobCardId || '',
        branchId: quotation.branchId,
        quotationDate: quotation.quotationDate.split('T')[0],
        expiryDate: quotation.expiryDate.split('T')[0],
        notes: (quotation.notes || '')
          .replace(/&#x2F;/g, '/')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&#x27;/g, "'")
          .replace(/&quot;/g, '"')
          .split('\n').filter(line => 
          !line.match(/^\[CreatedBy:/) && 
          !line.match(/^\[.*\] Edited by/) &&
          !line.match(/^\[.*\] Updated by/) &&
          !line.match(/^\[.*\] Status changed/) &&
          !line.match(/^\[.*\] Job card/) &&
          !line.match(/^\[.*\] Quotation created by/) &&
          !line.match(/^\[.*\] Created by/)
        ).join('\n').trim(),
        termsConditions: quotation.termsConditions || 'Validity – This quotation is valid for expiry date mentioned in Quotation Details above.\n\nPricing – Prices are subject to change if parts, labor rates, or taxes vary before approval.\n\nApproval – Work will commence only after written approval of this quotation.\n\nPayment – Payment terms as per agreement (e.g., 50% advance, balance upon completion).\n\nParts Availability – Quoted parts are subject to availability at the time of approval.\n\nWarranty – Warranty applies only to parts and labor specified in the approved job card.\n\nExclusions – Additional repairs or parts not listed will be charged separately.\n\nLiability – The company is not liable for delays caused by parts unavailability or unforeseen issues.',
        discount: quotation.discount
      });
      setItems(quotation.items || [{ type: 'part', itemId: '', description: '', quantity: 1, price: 0, total: 0 }]);
    } catch (error) {
      toast.error('Failed to load quotation');
    }
  };

  const fetchVehicles = async (customerId) => {
    try {
      const response = await axios.get(`/api/vehicles?customerId=${customerId}`);
      setVehicles(response.data.vehicles || response.data || []);
    } catch (error) {
      setVehicles([]);
    }
  };

  const fetchJobCards = async (customerId) => {
    try {
      const response = await axios.get('/api/job-cards/available');
      const jobCardsData = response.data || [];
      // Filter by customer if customerId is provided
      const filteredJobCards = customerId ? 
        jobCardsData.filter(jc => jc.customerId === parseInt(customerId)) : 
        jobCardsData;
      setJobCards(filteredJobCards);
    } catch (error) {
      setJobCards([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    setFormData({ ...formData, customerId, vehicleId: '', jobCardId: '' });
    if (customerId) {
      fetchVehicles(customerId);
      fetchJobCards(customerId);
    } else {
      setVehicles([]);
      setJobCards([]);
    }
  };

  const handleJobCardChange = async (jobCardId) => {
    if (!jobCardId) {
      setFormData({ ...formData, jobCardId: '' });
      return;
    }

    try {
      const response = await axios.get(`/api/jobcards/${jobCardId}`);
      const jobCard = response.data;
      
      // Auto-populate form fields from job card
      // Strip history entries from job card comments - only keep user notes
      const jobCardNotes = (jobCard.comments || '').split('\n').filter(line => 
        !line.match(/^\[.*\] Updated by/) &&
        !line.match(/^\[.*\] Status changed/) &&
        !line.match(/^\[.*\] Job card/) &&
        !line.match(/^\[.*\] Created by/) &&
        !line.match(/^\[.*\] Edited by/) &&
        !line.match(/^\[CreatedBy:/)
      ).join('\n').trim();
      
      setFormData({
        ...formData,
        jobCardId,
        customerId: jobCard.customerId || '',
        vehicleId: jobCard.vehicleId || '',
        vehicleNumber: jobCard.vehicleNumber || '',
        vinNumber: jobCard.vinNumber || '',
        odometerReading: jobCard.odometerReading || '',
        branchId: jobCard.branchId || formData.branchId,
        notes: jobCardNotes
      });

      // Set selected vehicle if available
      if (jobCard.vehicle) {
        setSelectedVehicle({
          id: jobCard.vehicle.id,
          brand: jobCard.vehicle.make,
          model: jobCard.vehicle.model,
          plateNumber: jobCard.vehicle.plateNumber
        });
      }

      // Auto-populate items from job card services and parts
      const jobCardItems = [];
      
      if (jobCard.services && jobCard.services.length > 0) {
        jobCard.services.forEach(service => {
          jobCardItems.push({
            type: 'service',
            itemId: service.serviceId || '',
            description: service.name || service.description || '',
            quantity: service.quantity || 1,
            price: service.price || 0,
            total: (service.quantity || 1) * (service.price || 0)
          });
        });
      }

      if (jobCard.parts && jobCard.parts.length > 0) {
        jobCard.parts.forEach(part => {
          jobCardItems.push({
            type: 'part',
            itemId: part.partId || '',
            description: part.name || part.description || '',
            quantity: part.quantity || 1,
            price: part.price || 0,
            total: (part.quantity || 1) * (part.price || 0)
          });
        });
      }

      if (jobCardItems.length > 0) {
        setItems(jobCardItems);
      }

      // Fetch related data
      if (jobCard.customerId) {
        fetchVehicles(jobCard.customerId);
        fetchJobCards(jobCard.customerId);
      }

    } catch (error) {
      toast.error('Failed to load job card details');
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-fill description and price when item is selected
    if (field === 'itemId' && value) {
      const itemId = parseInt(value);
      const selectedItem = newItems[index].type === 'part' 
        ? parts.find(p => p.id === itemId)
        : services.find(s => s.id === itemId);
      
      if (selectedItem) {
        newItems[index].description = selectedItem.name || selectedItem.description || '';
        newItems[index].price = parseFloat(selectedItem.price || selectedItem.costPrice || selectedItem.retailPrice || 0);
        newItems[index].total = newItems[index].quantity * newItems[index].price;
      }
    }
    
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = newItems[index].quantity * newItems[index].price;
    }
    
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { type: 'part', itemId: '', description: '', quantity: 1, price: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const discount = parseFloat(formData.discount) || 0;
    const vatPercentage = organization && organization.vatPercentage !== null ? organization.vatPercentage : 5;
    const tax = (subtotal - discount) * (vatPercentage / 100);
    const total = subtotal - discount + tax;
    
    return { subtotal, discount, tax, total, vatPercentage };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const quotationData = {
        ...formData,
        customerId: formData.customerId || null,
        branchId: formData.branchId || null,
        vehicleId: formData.vehicleId || null,
        jobCardId: formData.jobCardId || null,
        notes: formData.notes || '',
        termsConditions: formData.termsConditions || '',
        items: items.filter(item => item.description.trim() || item.itemId)
      };

      // Remove empty fields
      Object.keys(quotationData).forEach(key => {
        if (quotationData[key] === '' || quotationData[key] === null) {
          delete quotationData[key];
        }
      });


      if (isEdit) {
        await axios.put(`/api/quotations/${id}`, quotationData);
        toast.success('Quotation updated successfully');
        navigate(`/accounts/quotations/${id}`);
      } else {
        const response = await axios.post('/api/quotations', quotationData);
        toast.success('Quotation created successfully');
        const newId = response.data?.data?.id || response.data?.id;
        navigate(`/accounts/quotations/${newId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save quotation');
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, discount, tax, total, vatPercentage } = calculateTotals();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Quotation' : 'Create Quotation'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Card
              </label>
              <select
                value={formData.jobCardId || ''}
                onChange={(e) => handleJobCardChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Select Job Card</option>
                {Array.isArray(jobCards) && jobCards.map(jobCard => (
                  <option 
                    key={jobCard.id} 
                    value={jobCard.id}
                    title={`Customer: ${jobCard.customer?.name || 'N/A'} | Vehicle: ${jobCard.vehicle?.make || ''} ${jobCard.vehicle?.model || ''} - ${jobCard.vehicle?.plateNumber || 'N/A'}`}
                  >
                    {jobCard.orderNo}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer *
              </label>
              <select
                value={formData.customerId || ''}
                onChange={(e) => handleCustomerChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Customer</option>
                {Array.isArray(customers) && customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch *
              </label>
              <select
                value={formData.branchId || ''}
                onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Branch</option>
                {Array.isArray(branches) && branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Number
              </label>
              <input
                type="text"
                value={formData.vehicleNumber}
                onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                placeholder="e.g., ABC-123"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                VIN#
              </label>
              <input
                type="text"
                value={formData.vinNumber}
                onChange={(e) => setFormData({ ...formData, vinNumber: e.target.value })}
                placeholder="Vehicle Identification Number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ODO Meter
              </label>
              <input
                type="text"
                value={formData.odometerReading}
                onChange={(e) => setFormData({ ...formData, odometerReading: e.target.value })}
                placeholder="e.g., 50000 km"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle
              </label>
              <button
                type="button"
                onClick={() => setShowVehicleSelector(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-left hover:bg-gray-50"
              >
                {selectedVehicle ? (
                  <div>
                    <span className="font-medium">{selectedVehicle.brand} {selectedVehicle.model}</span>
                    {selectedVehicle.isNew && (
                      <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        New Vehicle
                      </span>
                    )}
                  </div>
                ) : 'Select Vehicle'}
              </button>
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quotation Date *
              </label>
              <input
                type="date"
                value={formData.quotationDate}
                onChange={(e) => setFormData({ ...formData, quotationDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date *
              </label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-3 items-center font-medium text-sm text-gray-700 border-b pb-2">
              <div className="col-span-2">Type</div>
              <div className="col-span-3">Item</div>
              <div className="col-span-2">Description</div>
              <div className="col-span-1">Qty</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-1">Total</div>
              <div className="col-span-1">Action</div>
            </div>
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-2">
                  <select
                    value={item.type || 'part'}
                    onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="part">Part</option>
                    <option value="service">Service</option>
                  </select>
                </div>
                <div className="col-span-3">
                  <select
                    value={item.itemId || ''}
                    onChange={(e) => handleItemChange(index, 'itemId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select {item.type}</option>
                    {item.type === 'part' 
                      ? Array.isArray(parts) && parts.map(part => (
                          <option key={part.id} value={part.id}>
                            {part.name} - {formatCurrency(part.price || part.costPrice || 0, organization?.currency_code)}
                          </option>
                        ))
                      : Array.isArray(services) && services.map(service => (
                          <option key={service.id} value={service.id}>
                            {service.name} - {formatCurrency(service.price || 0, organization?.currency_code)}
                          </option>
                        ))
                    }
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="col-span-1">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    placeholder="Price"
                    value={item.price}
                    onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="col-span-1">
                  <input
                    type="text"
                    value={formatCurrency(item.total || 0, organization?.currency_code)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-900"
                    disabled={items.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 border-t pt-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(subtotal, organization?.currency_code)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Discount:</span>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="flex justify-between">
                  <span>Tax ({vatPercentage}%):</span>
                  <span>{formatCurrency(tax, organization?.currency_code)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(total, organization?.currency_code)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Additional Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                value={formData.termsConditions || 'Validity – This quotation is valid for expiry date mentioned in Quotation Details above.\n\nPricing – Prices are subject to change if parts, labor rates, or taxes vary before approval.\n\nApproval – Work will commence only after written approval of this quotation.\n\nPayment – Payment terms as per agreement (e.g., 50% advance, balance upon completion).\n\nParts Availability – Quoted parts are subject to availability at the time of approval.\n\nWarranty – Warranty applies only to parts and labor specified in the approved job card.\n\nExclusions – Additional repairs or parts not listed will be charged separately.\n\nLiability – The company is not liable for delays caused by parts unavailability or unforeseen issues.'}
                onChange={(e) => setFormData({ ...formData, termsConditions: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="8"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/accounts')}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Quotation'}
          </button>
        </div>
      </form>

      {/* Vehicle Selector Modal */}
      {showVehicleSelector && (
        <VehicleSelector
          customerId={formData.customerId}
          onSelect={(vehicle) => {
            setSelectedVehicle(vehicle);
            setFormData(prev => ({ ...prev, vehicleId: vehicle.id }));
            setShowVehicleSelector(false);
          }}
          onClose={() => setShowVehicleSelector(false)}
        />
      )}
    </div>
  );
};

export default QuotationForm;
